<template>
  <div class="docs-wrapper q-pa-md">

    <div class="text-h4 text-weight-bold q-mb-xs">
      Медицинские документы
    </div>
<!--
    <div v-if="patientName" class="text-subtitle1 text-weight-medium q-mb-lg">
      {{ patientName }}
    </div> -->

    <!-- <q-banner
      class="info-banner q-mb-lg"
      rounded
    >
      <template #avatar>
        <div class="info-accent-bar" />
      </template>
      В личный кабинет приходят только документы, выданные после 1 января 2024 г.
      <br />
      <a href="#" class="text-primary">Как посмотреть документы</a>
    </q-banner> -->

    <div class="segment-toggle q-mb-md">
      <button
        :class="['segment-btn', !viewByVisits ? 'segment-btn--active' : '']"
        @click="viewByVisits = false"
      >
        <q-icon name="filter_list" size="16px" class="q-mr-xs" />
        По категориям
      </button>
      <button
        :class="['segment-btn', viewByVisits ? 'segment-btn--active' : '']"
        @click="viewByVisits = true"
      >
        <q-icon name="date_range" size="16px" class="q-mr-xs" />
        По визитам
      </button>
    </div>

    <div v-if="!viewByVisits" class="q-mb-lg">

      <div class="filter-label q-mb-xs">Выберите категорию</div>
      <q-select
        v-model="selectedCategory"
        :options="categoryOptions"
        outlined
        behavior="menu"
        dense
        emit-value
        map-options
        class="q-mb-md filter-select"
      />

      <div class="filter-label q-mb-xs">Период</div>
      <q-input
        v-model="periodText"
        outlined
        dense
        mask="##.##.#### — ##.##.####"
        fill-mask
        :error="!!errorMessage"
        :error-message="errorMessage"
        @blur="parseInput"
        class="filter-input"
      >
        <template #append>
          <q-icon name="event" color="primary" class="cursor-pointer">
            <q-popup-proxy>
              <q-date
                v-model="dateRange"
                range
                mask="DD.MM.YYYY"
                color="accent"
                :options="dateRangeOptions"
              />
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
    </div>

    <div v-if="loading" class="flex flex-center q-pa-xl">
      <q-spinner size="40px" color="primary" />
    </div>

    <div
      v-if="!loading && documents.length === 0"
      class="text-grey text-body2"
    >
      Документы отсутствуют
    </div>

    <div v-if="!viewByVisits && !loading">
      <div
        v-for="doc in filteredByCategoryAndPeriod"
        :key="doc.id_document"
        class="doc-card q-mb-sm"
        @click="openInNewTab(doc)"
      >
        <div class="doc-date-label text-caption text-grey q-mb-xs">
          {{ doc.date_doc }}
        </div>
        <div class="doc-title">
          {{ doc.doc_name }}
        </div>

        <div class="doc-actions" @click.stop>
          <q-btn flat round dense icon="more_vert">
            <q-menu>
              <q-list style="min-width: 140px">
                <q-item clickable v-close-popup @click="download(doc)">
                  <q-item-section>Скачать</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </div>
    </div>

    <div v-else-if="!loading">
      <div
        v-if="groupedByVisit.length === 0"
        class="text-grey"
      >
        Документы по визитам отсутствуют
      </div>

      <div
        v-for="group in groupedByVisit"
        :key="group.date"
        class="visit-group q-mb-lg"
      >
        <div class="visit-header q-mb-sm">
          Визит {{ group.label }}
        </div>

        <div
          v-for="doc in group.docs"
          :key="doc.id_document"
          class="doc-card q-mb-sm"
          @click="openInNewTab(doc)"
        >
          <div class="doc-date-label text-caption text-grey q-mb-xs">
            {{ doc.date_doc }}
          </div>
          <div class="doc-title">
            {{ doc.doc_name }}
          </div>
          <div class="doc-actions" @click.stop>
            <q-btn flat round dense icon="more_vert">
              <q-menu>
                <q-list style="min-width: 140px">
                  <q-item clickable v-close-popup @click="download(doc)">
                    <q-item-section class="text-accent">Скачать</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { User } from '@/store/auth/types';
import {
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
// eslint-disable-next-line import/no-cycle
import { useEcoHospitalStore } from '~store/ecohospital/ecohospitalModule';

// eslint-disable-next-line import/no-cycle
import { useMainStore } from '~store/index';

export default { name: 'PatientDocuments' };
</script>

<script setup lang="ts">
export type UserDocument = {
  att_type: number;
  checksum: number;
  date_doc: string;
  datebegin: string;
  doc_name: string;
  doc_type: number;
  file_ext: string;
  id_document: number;
  id_patient: number;
  is_napravlenie: number;
  tg: string;
};

export type GroupedDocuments = {
  header: string;
  documents: UserDocument[];
};
const ecoStore = useEcoHospitalStore();

const documents = ref<UserDocument[]>([]);
const loading = ref(false);

const MIN_DATE = new Date(2019, 0, 1);
const TODAY = new Date();

onMounted(async () => {
  try {
    loading.value = true;
    documents.value = await ecoStore.getDocuments();
  } catch (error) {
    console.error('Ошибка при загрузке документов:', error);
  } finally {
    loading.value = false;
  }
});

const viewByVisits = ref(false);

const categoryOptions = [
  { label: 'Направления', value: 'directions' },
  { label: 'Лабораторные анализы', value: 'labs' },
  { label: 'Остальные документы', value: 'others' },
];

const selectedCategory = ref('directions');

const formatDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const parseDate = (str: string): Date | null => {
  const [d, m, y] = str.split('.').map(Number);
  if (!d || !m || !y) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};
const dateRange = ref<{ from: string; to: string } | null>({
  from: '01.01.2024',
  to: formatDate(TODAY),
});

const periodText = ref(
  `${dateRange.value?.from} — ${dateRange.value?.to}`,
);

const errorMessage = ref('');

const dateRangeOptions = (date: string) => {
  const [y, m, d] = date.split('/').map(Number);
  const dt = new Date(y, m - 1, d);

  return dt >= MIN_DATE && dt <= TODAY;
};

watch(dateRange, (val: { to: string, from: string } | null) => {
  if (!val?.from || !val?.to) return;

  const from = parseDate(val.from);
  const to = parseDate(val.to);

  if (!from || !to) return;

  if (from < MIN_DATE || to > TODAY) {
    errorMessage.value = 'Период должен быть с 01.01.2019 по сегодняшний день';
    return;
  }

  errorMessage.value = '';
  periodText.value = `${val.from} — ${val.to}`;
});

const categoryFiltered = computed(() => {
  if (!documents.value.length) return [];

  return documents.value.filter((doc) => {
    if (selectedCategory.value === 'directions') return doc.is_napravlenie === 1;
    if (selectedCategory.value === 'labs') return doc.doc_type === 36;
    if (selectedCategory.value === 'others') return doc.is_napravlenie !== 1 && doc.doc_type !== 36;
    return true;
  });
});

const filteredByCategoryAndPeriod = computed(() => {
  if (!documents.value.length) return [];

  let from = new Date(2024, 0, 1);
  let to = new Date(2099, 11, 31);

  if (dateRange.value?.from && dateRange.value?.to) {
    const parsedFrom = parseDate(dateRange.value.from);
    const parsedTo = parseDate(dateRange.value.to);

    if (parsedFrom && parsedTo) {
      from = parsedFrom;
      to = parsedTo;
    }
  }

  return categoryFiltered.value
    .filter((doc) => {
      const d = parseDate(doc.date_doc);
      if (!d) return false;
      return d >= from && d <= to;
    })
    .sort(
      (a, b) => parseDate(b.date_doc)!.getTime()
        - parseDate(a.date_doc)!.getTime(),
    );
});

const parseInput = () => {
  const parts = periodText.value.split('—').map((s) => s.trim());
  if (parts.length !== 2) return;

  const from = parseDate(parts[0]);
  const to = parseDate(parts[1]);

  if (!from || !to) {
    errorMessage.value = 'Некорректный формат даты';
    return;
  }

  if (from < MIN_DATE || to > TODAY) {
    errorMessage.value = 'Доступный для выбора период с 01.01.2019 по сегодняшний день';
    return;
  }

  errorMessage.value = '';
  dateRange.value = {
    from: formatDate(from),
    to: formatDate(to),
  };
};

const NOT_STARTED_KEY = '__not_started__';

const groupedByVisit = computed(() => {
  const map = new Map<string, UserDocument[]>();

  documents.value.forEach((doc) => {
    const key = doc.datebegin ?? NOT_STARTED_KEY;

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(doc);
  });

  const groups = Array.from(map.entries())
    .map(([date, docs]) => ({
      date,
      label: date === NOT_STARTED_KEY ? 'без даты' : date,
      docs,
    }))
    .sort((a, b) => {
      if (a.date === NOT_STARTED_KEY) return -1;
      if (b.date === NOT_STARTED_KEY) return 1;

      const da = parseDate(a.date);
      const db = parseDate(b.date);

      if (da && db) return db.getTime() - da.getTime();

      if (db) return 1;
      if (da) return -1;

      return 0;
    });

  return groups;
});

const openInNewTab = async (doc: UserDocument) => {
  const path = await ecoStore.getFilePath(doc.id_document);
  if (path[0].hash) {
    window.open(`https://host2.medsafe.tech/${path[0].hash}`, '_tab');
  } else {
    useMainStore().showMessage('Извините, не смогли найти документ.');
  }
};

const download = async (doc: UserDocument) => {
  const path = await ecoStore.getFilePath(doc.id_document);
  const fileUrl = `https://host2.medsafe.tech/${path[0].hash}`;

  const response = await fetch(fileUrl);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = doc.doc_name;
  link.click();

  URL.revokeObjectURL(blobUrl);
};
</script>

<style scoped>
.docs-wrapper {
  max-width: 600px;
  margin: 0 auto;
}

.info-banner {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
}

.info-accent-bar {
  width: 4px;
  height: 100%;
  background: #1976d2;
  border-radius: 4px;
  position: absolute;
  left: 0;
  top: 0;
}

.filter-label {
  font-size: 14px;
  color: #6b7280;
}

.filter-select,
.filter-input {
  background: #fff;
}

.doc-card {
  position: relative;
  padding: 16px 48px 16px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.15s ease;
}

.doc-card:hover {
  background: #f0f4ff;
}

.doc-card:active {
  background: #e8eef8;
}

.doc-date-label {
  font-size: 13px;
  color: #9ca3af;
}

.doc-title {
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  line-height: 1.35;
}

.doc-org {
  font-size: 13px;
  color: #6b7280;
}

.doc-actions {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

.visit-header {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}
.segment-toggle {
  display: inline-flex;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
}

.segment-btn {
  display: flex;
  align-items: center;
  padding: 8px 18px;
  border: none;
  border-radius: 7px;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  background: transparent;
  color: #555;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  white-space: nowrap;
}

.segment-btn--active {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.segment-btn:hover:not(.segment-btn--active) {
  background: rgba(255, 255, 255, 0.5);
}
</style>
